import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InventoryIcon from '@mui/icons-material/Inventory';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AppsIcon from '@mui/icons-material/Apps';
import PersonIcon from '@mui/icons-material/Person';
import EngineeringIcon from '@mui/icons-material/Engineering';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BuildIcon from '@mui/icons-material/Build';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HubIcon from '@mui/icons-material/Hub';
import RadarIcon from '@mui/icons-material/Radar';
import PollIcon from '@mui/icons-material/Poll';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const iconMap = {
  inventory: InventoryIcon,
  security: SecurityIcon,
  support_agent: SupportAgentIcon,
  apps: AppsIcon,
  analytics: AnalyticsIcon,
  automation: AutoFixHighIcon,
  phone: PhoneInTalkIcon,
  document: DescriptionIcon,
  admin: AccountBalanceIcon,
  build: BuildIcon,
  assessment: AssessmentIcon,
  bot: PsychologyIcon,
  hub: HubIcon,
  radar: RadarIcon,
  poll: PollIcon,
  settings: SettingsSuggestIcon,
  ai: AutoAwesomeIcon,
};

const statusConfig = {
  production: { color: '#24a148', textColor: '#198038', label: 'Disponible' },
  development: { color: '#f1c21b', textColor: '#8a6d00', label: 'En desarrollo' },
  beta: { color: '#a56eff', textColor: '#8a3ffc', label: 'Beta' },
  deprecated: { color: '#fa4d56', textColor: '#da1e28', label: 'Deprecado' },
};

const AppCard = ({ app }) => {
  const IconComponent = iconMap[app.icon] || AppsIcon;
  const status = statusConfig[app.status] || statusConfig.production;

  return (
    <Tooltip
      title={app.description}
      arrow
      placement="top"
      enterDelay={600}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#161616',
            color: 'white',
            fontSize: '0.75rem',
            maxWidth: 320,
            p: 1.5,
            borderRadius: 0,
          },
        },
        arrow: { sx: { color: '#161616' } },
      }}
    >
      <Box
        component="a"
        href={app.url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 0,
          p: 2,
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -1,
            top: -1,
            bottom: -1,
            width: '3px',
            backgroundColor: '#0f62fe',
            transform: 'scaleY(0)',
            transformOrigin: 'top',
            transition: 'transform 0.18s ease',
          },
          '&:hover': {
            borderColor: '#0f62fe',
            boxShadow: '0 4px 16px rgba(15, 98, 254, 0.12)',
            '&::before': { transform: 'scaleY(1)' },
            '& .launch-arrow': { color: '#0f62fe' },
          },
          '&:focus-visible': {
            outline: '2px solid #0f62fe',
            outlineOffset: '2px',
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&::before': { transition: 'none' },
          },
        }}
      >
        {/* Header: icon + category/name + launch */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              backgroundColor: '#edf5ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconComponent sx={{ fontSize: 18, color: '#0f62fe' }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.6rem',
                fontWeight: 600,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: '#8d8d8d',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {app.category}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#161616',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {app.name}
            </Typography>
          </Box>
          <LaunchIcon
            className="launch-arrow"
            sx={{ fontSize: 15, color: '#c6c6c6', mt: 0.4, flexShrink: 0, transition: 'color 0.15s ease' }}
          />
        </Box>

        {/* Status + version */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: status.color,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: status.textColor }}>
            {status.label}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.68rem',
              color: '#a8a8a8',
              fontFamily: '"IBM Plex Mono", monospace',
              ml: 'auto',
            }}
          >
            v{app.version}
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: '#525252',
            lineHeight: 1.45,
            mb: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {app.description}
        </Typography>

        {/* Footer: owners + docs */}
        <Box
          sx={{
            mt: 'auto',
            pt: 1.25,
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <EngineeringIcon sx={{ fontSize: 13, color: '#8d8d8d', flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: '0.7rem', color: '#525252' }}>
                <Box component="span" sx={{ fontWeight: 600, color: '#393939' }}>Técnico</Box>
                {' · '}{app.technicalOwner?.name || '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 13, color: '#8d8d8d', flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: '0.7rem', color: '#525252' }}>
                <Box component="span" sx={{ fontWeight: 600, color: '#393939' }}>Operativo</Box>
                {' · '}{app.operationalOwner?.name || '—'}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Requerimientos de implementación">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(app.requirementsUrl || app.userGuideUrl || '#', '_blank');
              }}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 0,
                border: '1px solid #e0e0e0',
                color: '#525252',
                '&:hover': { borderColor: '#0f62fe', color: '#0f62fe', backgroundColor: '#edf5ff' },
              }}
            >
              <MenuBookIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default AppCard;
